import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/* ─── ACP Role badges — shared by the Podium tool pages (Bonus Checker,
   Optimove, ACP Quick View). Colour semantics mirror ACPPanel.getRoleColorClass
   so agents recognise roles instantly across Clara, while the pill + hover
   popup follow the Podium design language (flat + sharp, hairlines,
   ink/canvas). ─── */
const RC_RED = 'bg-red-500/15 text-red-500 dark:bg-red-500/20 dark:text-red-400 border-red-500/20';
const RC_RED_DEEP = 'bg-red-600/15 text-red-600 dark:bg-red-600/20 dark:text-red-400 border-red-600/20';
const RC_ORANGE = 'bg-orange-500/15 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400 border-orange-500/20';
const RC_AMBER = 'bg-amber-500/15 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20';
const RC_BLUE = 'bg-blue-500/15 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 border-blue-500/20';
const RC_SKY = 'bg-sky-400/15 text-sky-500 dark:bg-sky-400/20 dark:text-sky-300 border-sky-400/20';
const RC_PURPLE = 'bg-purple-500/15 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400 border-purple-500/20';
const RC_GREEN = 'bg-emerald-500/15 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20';
const RC_WHITE = 'bg-gray-100 text-gray-700 dark:bg-white/[0.15] dark:text-white border-gray-300/60 dark:border-white/30';
const RC_DEFAULT = 'bg-gray-500/15 text-gray-600 dark:bg-gray-500/20 dark:text-gray-500 border-gray-500/20';
const ROLE_COLOR_MAP = {
  banned: RC_RED_DEEP, suspended: RC_RED, breakinplay: RC_RED, selfexclusion: RC_RED, selfexcluded: RC_RED,
  fiatsuspended: RC_RED, frozen: RC_RED, compromised: RC_RED, suspendedsportsbook: RC_RED,
  suspendedlevel1: RC_RED, suspendedlevel2: RC_RED, suspendedlevel3: RC_RED,
  enhancedduedilligence: RC_RED, enhancedduedilligencefrozen: RC_RED, houseexcluded: RC_RED, pokerexcluded: RC_RED,
  suspendedsanctions: RC_ORANGE, owner: RC_ORANGE, communitymanager: RC_ORANGE,
  suspendedtipping: RC_AMBER, muted: RC_AMBER,
  periodicbonusabuser: RC_WHITE, promotionabuser: RC_WHITE, optimoveabuser: RC_WHITE, rainabuser: RC_WHITE,
  bonusabuser: RC_WHITE, abuser: RC_WHITE, livecasinorestricted: RC_WHITE, hiddensportbetstream: RC_WHITE,
  sportsbookmaxbet: RC_WHITE, sportsbookfraudforceflagged: RC_WHITE, casinofraudforceflagged: RC_WHITE,
  norainlimit: RC_SKY, notiplimit: RC_SKY, whitelist: RC_BLUE, whitelisted: RC_BLUE, moderator: RC_BLUE,
  admin: RC_PURPLE, vipmanager: RC_PURPLE, affiliatemanager: RC_GREEN, fiatwithdrawonly: RC_GREEN,
};
// Pre-sort by key length descending so partial-match picks the most specific key
// first (e.g. `suspendedsanctions` wins over `suspended`).
const ROLE_ENTRIES = Object.entries(ROLE_COLOR_MAP).sort((a, b) => b[0].length - a[0].length);
export const getRoleColorClass = (name) => {
  const k = (name || '').toLowerCase().replace(/[^a-z]/g, '');
  if (ROLE_COLOR_MAP[k]) return ROLE_COLOR_MAP[k];
  const m = ROLE_ENTRIES.find(([key]) => k.includes(key));
  return m ? m[1] : RC_DEFAULT;
};

// Text-colour-only subset of the role colour (for the bare-letter variant —
// no pill box, just the coloured letter).
export const getRoleTextClass = (name) =>
  getRoleColorClass(name).split(' ').filter((c) => c.startsWith('text-') || c.startsWith('dark:text-')).join(' ');

/**
 * Relative expiry, same wording as the ACP profile header ("in 3 days" /
 * "expired").
 *
 * ROUNDS UP, because ACP does. This used to floor, and floor and ceil agree
 * only when the remaining time is an exact multiple of the unit — so Clara was
 * showing a smaller number than ACP for very nearly every role on screen, not
 * in some rare edge case. Filip caught it on a bonusAbuser with 53.31 h left:
 * ACP "in 3 days", Clara "in 2 days" (ceil 2.221 → 3, floor → 2).
 *
 * Ceiling is also the honest reading of the thing being described. The role is
 * still in force for part of that last day; "in 2 days" tells an agent it is
 * gone a whole day before it is. The old floor had the same shape of bug at
 * the bottom of the scale — thirty seconds left printed "in 0 min".
 *
 * Each tier ceils from the RAW difference and promotes if that overflows the
 * unit, so 59 m 30 s reads "in 1 hr" rather than "in 60 min", and 6 d 20 h
 * reads "in 1 week" rather than "in 7 days".
 */
const MIN_MS = 60000;
const HR_MS = 60 * MIN_MS;
const DAY_MS = 24 * HR_MS;
const WEEK_MS = 7 * DAY_MS;

export function fmtRoleExpiry(ts) {
  if (!ts) return null;
  const d = new Date(/^\d+$/.test(String(ts)) ? Number(ts) : ts);
  if (isNaN(d)) return null;
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return 'expired';
  const mins = Math.ceil(diff / MIN_MS);
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.ceil(diff / HR_MS);
  if (hrs < 24) return `in ${hrs} hr${hrs > 1 ? 's' : ''}`;
  const days = Math.ceil(diff / DAY_MS);
  if (days < 7) return `in ${days} day${days > 1 ? 's' : ''}`;
  const weeks = Math.ceil(diff / WEEK_MS);
  return `in ${weeks} week${weeks > 1 ? 's' : ''}`;
}

/* Role pill with hover popup. The popup is PORTALED to <body> at fixed
   viewport coordinates, so it survives overflow/scroll containers (e.g. the
   Quick View's single-line scrollable roles row) and transformed (draggable)
   ancestors — a child-absolute popup would be clipped by either. A short
   close-delay keeps it open while the cursor crosses the gap into the popup,
   so the reason text stays selectable/copyable.

   variant="text"   → full role name in the pill (Bonus Checker / Optimove);
                      popup only when there is extra info (expiry / reason).
   variant="letter" → bare coloured first letter, no pill box around it;
                      popup ALWAYS shows so the full role name is readable. */
export function RoleBadge({ role, variant = 'text' }) {
  const [popupPos, setPopupPos] = useState(null); // { top, left } viewport coords
  const closeTimer = useRef(null);
  const expiry = fmtRoleExpiry(role.expireAt);
  const hasPopup = variant === 'letter' || !!(expiry || role.message);

  const openPopup = useCallback((e) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    const r = e.currentTarget.getBoundingClientRect();
    setPopupPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 360) });
  }, []);
  const scheduleClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPopupPos(null), 140);
  }, []);
  const cancelClose = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);

  return (
    <span
      onMouseEnter={hasPopup ? openPopup : undefined}
      onMouseLeave={hasPopup ? scheduleClose : undefined}
      className={
        variant === 'letter'
          ? `w-4 h-5 text-[13px] font-black uppercase cursor-default inline-flex items-center justify-center shrink-0 ${getRoleTextClass(role.name)}`
          : `inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-[0.04em] cursor-default shrink-0 whitespace-nowrap ${getRoleColorClass(role.name)}`
      }
    >
      {variant === 'letter' ? (role.name || '?')[0] : role.name}
      {popupPos && createPortal(
        <span
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left, zIndex: 95 }}
          className="block px-3.5 py-2.5 w-max max-w-[340px] border border-[#cacacb] dark:border-white/[0.15] bg-white dark:bg-[#161616] shadow-[0_8px_24px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.55)] text-[11.5px] leading-relaxed text-[#39393b] dark:text-white/75 select-text cursor-text"
        >
          <span className="font-bold uppercase tracking-[0.08em] text-[10px] text-[#111111] dark:text-white">{role.name}</span>
          {expiry && <span className="block mt-1"><span className="font-bold text-[#111111] dark:text-white">Expires:</span> {expiry}</span>}
          {role.message && <span className="block mt-1"><span className="font-bold text-[#111111] dark:text-white">Reason:</span> {role.message}</span>}
        </span>,
        document.body
      )}
    </span>
  );
}
